import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import { Link, Redirect, useLocation } from "react-router-dom";
import axios from "axios";
import {
  FaTasks,
  FaPlus,
  FaSearch,
  FaSyncAlt,
  FaEdit,
  FaEye,
  FaChartBar,
  FaCalendarAlt,
  FaCog,
  FaUserEdit,
  FaBookOpen,
  FaListAlt,
  FaUsers,
  FaCopy,
  FaHome,
  FaChevronRight,
  FaPoll,
} from "react-icons/fa";
import MyLoader from "../Spinner/MyLoader";
import SessionManager from "../Utils/SessionManager";
import myToast from "../Utils/MyToast";
import COLORS from "../resources/colors/COLORS";
import MdlSelectSession from "../dialog/MdlSelectSession";
import MdlEditExam from "../dialog/MdlEditExam";
import MdlUpdateStatus from "../dialog/MdlUpdateStatus";
import MdlCopyAndCreateExam from "../dialog/MdlCopyAndCreateExam";
import BackButton from "../ChildComponent/BackButton";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/payroll.css";

const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

const CT_HERO = `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 55%, ${COLORS.primary_color_shade9} 100%)`;

const EXAM_STATUS = { UPCOMING: "1", LIVE: "2", OVER: "3" };

function normalizeExamType(value) {
  const v = String(value || "").trim().toUpperCase().replace(/\s+/g, "_");
  return v === "CLASS_TEST" || v === "CLASSTEST" ? "CLASS_TEST" : "EXAM";
}

function statusTone(status) {
  if (status === EXAM_STATUS.UPCOMING) return "upcoming";
  if (status === EXAM_STATUS.LIVE) return "live";
  return "over";
}

function getParticipatingClasses(item) {
  const raw = item?.participating_class_list;
  if (!raw) return { count: 0, names: [] };
  if (Array.isArray(raw)) {
    const names = raw
      .map((c) => {
        if (typeof c === "object" && c !== null) {
          const cls = c.class_name || c.name || c.class_section_name || c.class_id || "";
          const sec = c.section_name || c.section_id || "";
          if (cls && sec) return `${cls} - ${sec}`;
          return cls || sec || "Unknown";
        }
        return String(c);
      })
      .map((n) => String(n || "").trim())
      .filter(Boolean);
    return { count: names.length, names };
  }
  if (typeof raw === "string") {
    const names = raw.split(",").map((s) => s.trim()).filter(Boolean);
    return { count: names.length, names };
  }
  return { count: 0, names: [] };
}

const QUICK_ACTIONS = [
  { path: "ExamPaperSettings", label: "Paper Settings", icon: FaCog },
  { path: "ExamAddMarkSubjectWise", label: "Mark-Subject", icon: FaBookOpen },
  { path: "ExamAddMarkStudentWise", label: "Mark-Student", icon: FaUserEdit },
  { path: "ExamMarkingSystem", label: "View Marks", icon: FaChartBar },
  { path: "ExamDateSheet", label: "Date Sheet", icon: FaCalendarAlt },
  { path: "ExamGuideline", label: "Guideline", icon: FaListAlt },
];

//Created By Cursor
const ClassTestCard = memo(function ClassTestCard({ exam, onEdit, onStatus }) {
  const classes = getParticipatingClasses(exam);
  const tone = statusTone(exam.status);
  const baseParams = `${exam.exam_id}/${exam.session_id}/${encodeURIComponent(
    exam.exam_name || ""
  )}/${encodeURIComponent(exam.status_name || "")}`;

  return (
    <article className={`ct-card ct-card--${tone}`}>
      <div className="ct-card__accent" />
      <div className="ct-card__body">
        <div className="ct-card__header">
          <div className="ct-card__title-area">
            <h3 className="ct-card__name">{exam.exam_name || "Untitled"}</h3>
            <span className={`ct-card__status ct-card__status--${tone}`}>
              {exam.status_name || "—"}
            </span>
          </div>
          <div className="ct-card__class-pills">
            <span className="ct-card__class-count">
              <FaUsers aria-hidden />
              <strong>{classes.count}</strong> classes
            </span>
            {classes.names.map((name, idx) => (
              <span key={`${name}-${idx}`} className="ct-card__class-pill" title={name}>
                {name}
              </span>
            ))}
          </div>
          {exam.exam_remark ? (
            <p className="ct-card__remark" title={exam.exam_remark}>
              {exam.exam_remark}
            </p>
          ) : null}
        </div>

        <div className="ct-card__actions">
          <button type="button" className="ct-act ct-act--edit" onClick={() => onEdit(exam)}>
            <FaEdit aria-hidden /> Edit
          </button>
          <Link to={`/AdminMain/ExamDetails/${baseParams}`} className="ct-act ct-act--view">
            <FaEye aria-hidden /> Details
          </Link>
          <button type="button" className="ct-act ct-act--status" onClick={() => onStatus(exam)}>
            <FaSyncAlt aria-hidden /> Status
          </button>
          {exam.status === EXAM_STATUS.OVER ? (
            <Link
              to={`/AdminMain/ExamClassResultStatus/${exam.exam_id}/${exam.session_id}/${encodeURIComponent(
                exam.exam_name || ""
              )}/${exam.start_date || ""}`}
              className="ct-act ct-act--result"
            >
              <FaChartBar aria-hidden /> Result
            </Link>
          ) : null}
        </div>

        <div className="ct-card__quick">
          {QUICK_ACTIONS.map((action) => {
            const Icon = action.icon;
            return (
              <Link
                key={action.path}
                to={`/AdminMain/${action.path}/${baseParams}`}
                className="ct-quick-link"
                title={action.label}
              >
                <Icon aria-hidden />
                <span>{action.label}</span>
                <FaChevronRight className="ct-quick-link__arrow" aria-hidden />
              </Link>
            );
          })}
        </div>
      </div>
    </article>
  );
});

//Created By Cursor
function ClassTestList() {
  const loggedIn = localStorage.getItem("token") !== null;
  const location = useLocation();

  const [isOpenSessionDialog, setIsOpenSessionDialog] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedExam, setSelectedExam] = useState(null);
  const [bootLoading, setBootLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [sessionId, setSessionId] = useState("");
  const [sessionName, setSessionName] = useState("");
  const [sessionStatus, setSessionStatus] = useState("");
  const [examList, setExamList] = useState([]);
  const [isUpdateStatusOpen, setIsUpdateStatusOpen] = useState(false);
  const [isCopyCreateOpen, setIsCopyCreateOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const getExamList = useCallback(async (session_id, { soft = false } = {}) => {
    if (!session_id) {
      setExamList([]);
      setBootLoading(false);
      setRefreshing(false);
      return;
    }
    if (soft) setRefreshing(true);
    else setBootLoading(true);
    const schoolCode = SessionManager.getSchoolCode();
    if (!schoolCode) {
      setExamList([]);
      myToast.notify_warning("School code is missing.");
      setBootLoading(false);
      setRefreshing(false);
      return;
    }
    try {
      const response = await axios.get(`${API_BASE()}getExamList.php`, {
        params: {
          school_code: schoolCode,
          session_id,
          _ts: Date.now(),
        },
        headers: { "Cache-Control": "no-cache" },
      });
      if (response.data?.api_status === "OK") {
        setExamList(response.data.data || []);
      } else {
        setExamList([]);
        myToast.notify_warning(response.data?.api_remark || "Could not load class tests.");
      }
    } catch (error) {
      console.error("Error fetching class test list:", error);
      setExamList([]);
      myToast.notify_warning("Network error loading class tests.");
    } finally {
      setBootLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    try {
      const mSession = SessionManager.getCurrentSession();
      const sid = mSession?.[0]?.session_id || "";
      setSessionId(sid);
      setSessionName(mSession?.[0]?.session_name || "");
      setSessionStatus(mSession?.[0]?.status || "");
      getExamList(sid);
    } catch (e) {
      console.error(e);
      setBootLoading(false);
      myToast.notify_warning("Could not load current session.");
    }
  }, [getExamList]);

  //Created By Cursor — refresh after returning from ExamCreation
  useEffect(() => {
    if (location.pathname !== "/AdminMain/ClassTestList") return;
    if (!location.state?.refreshExamList) return;
    const sid =
      sessionId ||
      SessionManager.getCurrentSession()?.[0]?.session_id ||
      "";
    if (sid) getExamList(sid, { soft: true });
  }, [location.pathname, location.state, sessionId, getExamList]);

  const handleSessionDialogToggle = () => setIsOpenSessionDialog((p) => !p);

  const handleSelectedSession = (sid, sname, sstatus) => {
    setSessionId(sid);
    setSessionName(sname);
    setSessionStatus(sstatus || "");
    setIsOpenSessionDialog(false);
    getExamList(sid);
  };

  const handleEditClick = (exam) => {
    setSelectedExam(exam);
    setIsEditModalOpen(true);
  };
  const handleEditSave = (sid) => {
    setIsEditModalOpen(false);
    getExamList(sid || sessionId, { soft: true });
  };
  const handleUpdateStatusOpen = (exam) => {
    setSelectedExam(exam);
    setIsUpdateStatusOpen(true);
  };
  const handleCloseModals = (sid) => {
    setIsUpdateStatusOpen(false);
    setSelectedExam(null);
    getExamList(sid || sessionId, { soft: true });
  };

  const visibleTests = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return examList
      .filter((e) => normalizeExamType(e.exam_type) === "CLASS_TEST")
      .filter((e) => {
        if (!q) return true;
        return [e.exam_name, e.exam_remark, e.status_name, e.exam_id]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(q);
      })
      .sort((a, b) =>
        String(a.exam_name || "").localeCompare(String(b.exam_name || ""), undefined, {
          sensitivity: "base",
        })
      );
  }, [examList, searchTerm]);

  const totalTests = useMemo(
    () => examList.filter((e) => normalizeExamType(e.exam_type) === "CLASS_TEST").length,
    [examList]
  );

  if (!loggedIn) return <Redirect to="/Login" />;

  return bootLoading ? (
    <MyLoader />
  ) : (
    <div
      className="content payroll-page sm-page ct-page"
      style={{
        background: COLORS.white,
        fontFamily:
          'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      }}
    >
      <div
        className="content-header d-flex justify-content-between align-items-center"
        style={{ background: COLORS.primary_color_shade5 }}
      >
        <div className="leftside-content-header d-flex align-items-center gap-2">
          <button
            type="button"
            className="qb-home-btn"
            aria-label="Home"
            onClick={() => window.history.back()}
          >
            <FaHome />
          </button>
          <ul className="breadcrumbs mb-0">
            <li>
              <Link to="/AdminMain/Academic/1" style={{ color: "#fff" }}>
                Academic
              </Link>
            </li>
            <li>
              <span style={{ color: "#fff" }}>Class Tests</span>
            </li>
          </ul>
        </div>
        <div className="rightside-content-header">
          <BackButton />
        </div>
      </div>

      <div className="bg_page_white_rounded box_shadow animated fadeInUp ct-shell">
        <header className="ct-hero" style={{ "--ct-hero-bg": CT_HERO }}>
          <div className="ct-hero__brand">
            <div className="ct-hero__icon" aria-hidden>
              <FaTasks />
            </div>
            <div className="min-w-0">
              <h1 className="ct-hero__title">Class Tests</h1>
              <p className="ct-hero__sub">
                {totalTests} class test{totalTests !== 1 ? "s" : ""} · {sessionName || "No session"}
              </p>
            </div>
          </div>

          <div className="ct-hero__tools">
            <button
              type="button"
              className="ct-session-pill"
              onClick={handleSessionDialogToggle}
              title="Change session"
            >
              <span className="ct-session-pill__label">Session</span>
              <span className="ct-session-pill__name">{sessionName || "Select"}</span>
              {sessionStatus ? (
                <span
                  className={`ct-session-pill__status${
                    String(sessionStatus).toLowerCase() === "active" || sessionStatus === "1"
                      ? " is-active"
                      : ""
                  }`}
                >
                  {sessionStatus}
                </span>
              ) : null}
            </button>
            <div className="ct-search-group">
              <FaSearch className="ct-search-group__icon" aria-hidden />
              <input
                type="search"
                className="ct-search-group__input"
                placeholder="Search class tests…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                aria-label="Search class tests"
              />
            </div>
            <button
              type="button"
              onClick={() => getExamList(sessionId, { soft: true })}
              disabled={refreshing || !sessionId}
              className={`ct-icon-btn${refreshing ? " spinning" : ""}`}
              title="Refresh"
              aria-label="Refresh"
            >
              <FaSyncAlt aria-hidden />
            </button>
            <button
              type="button"
              className="ct-hero-btn ct-hero-btn--outline"
              onClick={() => setIsCopyCreateOpen(true)}
              disabled={!sessionId}
            >
              <FaCopy aria-hidden /> Copy & Create
            </button>
            <Link
              to="/AdminMain/ExamCreation/?type=CLASS_TEST"
              className="ct-create-btn ct-create-btn--hero"
            >
              <span className="ct-create-btn__icon" aria-hidden>
                <FaPlus />
              </span>
              Create Class Test
            </Link>
          </div>
        </header>

        <div className="ct-content">
          {visibleTests.length === 0 ? (
            <div className="ct-empty">
              <div className="ct-empty__circle" aria-hidden>
                <FaTasks />
              </div>
              <p className="ct-empty__title">
                {totalTests === 0 ? "No class tests found" : "No matches for your search"}
              </p>
              <p className="ct-empty__hint">
                {totalTests === 0
                  ? "Create your first class test for this session."
                  : "Try a different name or status."}
              </p>
              {totalTests === 0 ? (
                <Link
                  to="/AdminMain/ExamCreation/?type=CLASS_TEST"
                  className="ct-create-btn ct-create-btn--primary"
                  style={{ marginTop: 14 }}
                >
                  <span className="ct-create-btn__icon" aria-hidden>
                    <FaPlus />
                  </span>
                  Create Class Test
                </Link>
              ) : null}
            </div>
          ) : (
            <div className="ct-list">
              {visibleTests.map((exam, idx) => (
                <ClassTestCard
                  key={exam.exam_id || `ct-${idx}`}
                  exam={exam}
                  onEdit={handleEditClick}
                  onStatus={handleUpdateStatusOpen}
                />
              ))}
            </div>
          )}

          <div className="ct-footer-cta">
            <Link
              to="/AdminMain/TestScore"
              className="ct-create-btn ct-create-btn--primary ct-create-btn--wide"
            >
              <span className="ct-create-btn__icon" aria-hidden>
                <FaPoll />
              </span>
              Average Test Score (ATS)
            </Link>
          </div>
        </div>
      </div>

      {isUpdateStatusOpen ? (
        <MdlUpdateStatus item={selectedExam} onClose={handleCloseModals} />
      ) : null}
      {isEditModalOpen && selectedExam ? (
        <MdlEditExam
          onClose={() => setIsEditModalOpen(false)}
          examName={selectedExam}
          onSave={handleEditSave}
        />
      ) : null}
      {isCopyCreateOpen ? (
        <MdlCopyAndCreateExam
          targetSessionId={sessionId}
          targetSessionName={sessionName}
          onClose={() => setIsCopyCreateOpen(false)}
          onSuccess={(sid) => getExamList(sid || sessionId, { soft: true })}
        />
      ) : null}
      {isOpenSessionDialog ? (
        <MdlSelectSession
          action={handleSessionDialogToggle}
          action2={handleSelectedSession}
        />
      ) : null}
    </div>
  );
}

export default ClassTestList;
